import { useAuthSession } from '../lib/authSession'
import DocumentPdf from './documents/DocumentPdf'
import TermsReviewCard from './documents/TermsReviewCard'
import PaymentObservationsCard from './documents/PaymentObservationsCard'
import './documents/reader.css'
import { useState, useEffect, useMemo, type CSSProperties } from 'react'
import { useParams, Link, useLocation, useSearchParams } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import { apiFetch, ApiError } from '../lib/api'
import { getToken, decodeToken } from '../lib/auth'
import { failureLabel } from '../lib/documents'
import { humanizeToken } from '../lib/officerFormat'
import PipelinePanel from './PipelinePanel'
import { fetchDocuments } from './documentsList'
import Sentence from './Sentence'
import { HelpDot } from './guidance/Guidance'
import type { RecordTablePayload } from './RecordTable'
import { chainOf, focusName, parseFocus, targetForFocus } from './fund/focus'
import EvidenceSentence from './EvidenceSentence'
import type { EvidenceTargetType } from '../lib/sentences/words'
import { translateText, useTranslationEnabled } from './translate/api'

/* Document page — IO ledger screen `document_detail`.
 * One document: the PDF itself (r-pdf, authed byte-stream) beside what
 * the pipeline did with it — every reasoning trace (r-trace, the
 * why-links), facts and status. */

interface DocRow {
  id: string
  filename: string
  document_type: string | null
  fund_names: string[]
  upload_date: string
  status: string
  page_count: number | null
  /** Detected source language (champion-2 #816). Only a non-'en' value is
   *  surfaced — as the amber "verify figures" advisory chip. */
  source_language: string | null
  /** Terminal failure reason (Gap 7) — validator vocabulary, only set on
   *  unprocessable/failed rows (encrypted = password-protected). */
  failure_reason: string | null
}

interface Trace {
  id: string
  agent_name: string
  pipeline_step: string | null
  call_type: string | null
  response_payload: Record<string, unknown> | null
  model_version: string | null
  latency_ms: number | null
  created_at: string
}

/** The `?page=` datum of a door: a positive whole number, or nothing —
 *  the viewer then opens at page 1 as it always did. */
function parsePage(raw: string | null): number | null {
  if (!raw || !/^\d+$/.test(raw)) return null
  const n = Number(raw)
  return n >= 1 ? n : null
}

/* The viewer (the browser's own PDF viewer on an authed byte-stream). A
 * door from a proven line carries the cited page: the viewer OPENS AT IT
 * ("document at 2 clicks", Ale 2026-09-03) through the `#page=N` open
 * parameter on the blob URL. `jump` remounts the viewer so the same page
 * can be re-jumped after the officer scrolled away. Nothing else is
 * highlighted (the passage highlight is deferred). */
function Pdf({
  documentId,
  page,
  jump,
}: {
  documentId: string
  page: number | null
  jump: number
}) {
  const session = useAuthSession()
  return (
    <DocumentPdf
      key={`${documentId}:${jump}`}
      documentId={documentId}
      session={session}
      page={page ?? 1}
      zoom={1}
      annotations={[]}
      selected={null}
      onSelect={() => {}}
      onPages={() => {}}
    />
  )
}

/* Officer actions (lifecycle #34c): reclassify / supersede / remove.
 * Each action posts to the document-admin router, which stamps a
 * RecordEvent; supersede and remove are destructive, so they require a
 * justification and an explicit confirm step. */

type ActionKind = 'reclassify' | 'supersede' | 'remove'

interface ActionResponse {
  document_id: string
  status: string
  document_type: string | null
  supersedes_document_id: string | null
}

const inputStyle: CSSProperties = {
  width: '100%',
  fontSize: 'var(--text-12)',
  padding: '6px 8px',
  border: '1px solid var(--line)',
  borderRadius: 7,
  boxSizing: 'border-box',
}

function ActionsCard({
  doc,
  allDocs,
  onApplied,
}: {
  doc: DocRow
  allDocs: DocRow[]
  onApplied: (update: Partial<DocRow>) => void
}) {
  const [open, setOpen] = useState<ActionKind | null>(null)
  const [types, setTypes] = useState<string[]>([])
  const [newType, setNewType] = useState('')
  const [supersedeTarget, setSupersedeTarget] = useState('')
  const [why, setWhy] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)

  useEffect(() => {
    apiFetch<{ document_types: string[] }>('/api/documents/types')
      .then((r) => setTypes(r.document_types))
      .catch(() => {})
  }, [])

  const toggle = (kind: ActionKind) => {
    setOpen((o) => (o === kind ? null : kind))
    setWhy('')
    setMsg(null)
  }

  const run = async (kind: ActionKind) => {
    if (!doc) return
    setBusy(true)
    setMsg(null)
    try {
      let body: Record<string, unknown>
      if (kind === 'reclassify') body = { document_type: newType, justification: why || null }
      else if (kind === 'supersede')
        body = { supersedes_document_id: supersedeTarget, justification: why }
      else body = { justification: why }

      const r = await apiFetch<ActionResponse>(`/api/documents/${doc.id}/${kind}`, {
        method: 'POST',
        body: JSON.stringify(body),
      })
      if (kind === 'reclassify') {
        onApplied({ document_type: r.document_type })
        setMsg({
          ok: true,
          text: `reclassified to ${humanizeToken(String(r.document_type))} — pipeline re-running`,
        })
      } else if (kind === 'supersede') {
        onApplied({})
        setMsg({ ok: true, text: 'older version marked superseded' })
      } else {
        onApplied({ status: r.status })
        setMsg({ ok: true, text: 'document removed (file bytes retained for audit)' })
      }
      setOpen(null)
    } catch (e) {
      const detail =
        e instanceof ApiError && e.body && typeof e.body === 'object' && 'detail' in e.body
          ? String((e.body as { detail: unknown }).detail)
          : e instanceof Error
            ? e.message
            : 'action failed'
      setMsg({ ok: false, text: detail })
    } finally {
      setBusy(false)
    }
  }

  const btn = (kind: ActionKind, label: string, danger = false) => (
    <button
      onClick={() => toggle(kind)}
      style={{
        fontSize: 'var(--text-12)',
        padding: '5px 10px',
        borderRadius: 7,
        border: `1px solid ${danger ? 'var(--rust)' : 'var(--line)'}`,
        color: danger ? 'var(--rust)' : 'inherit',
        background: open === kind ? '#f4f6f8' : '#fff',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  )

  const confirmBtn = (kind: ActionKind, label: string, disabled: boolean) => (
    <button
      onClick={() => run(kind)}
      disabled={disabled || busy}
      style={{
        fontSize: 'var(--text-12)',
        padding: '5px 12px',
        borderRadius: 7,
        border: 'none',
        background: disabled || busy ? '#b9c2cb' : 'var(--accent-ink)',
        color: '#fff',
        cursor: disabled || busy ? 'default' : 'pointer',
      }}
    >
      {busy ? 'working…' : label}
    </button>
  )

  if (doc.status === 'removed') {
    return (
      <div
        className="vcard"
        style={{ padding: 16, fontSize: 'var(--text-12)', color: 'var(--faint)' }}
      >
        This document was removed by an officer. The stored file is retained for audit.
      </div>
    )
  }

  const otherDocs = allDocs.filter((d) => d.id !== doc.id && d.status !== 'removed')

  return (
    <div className="vcard" style={{ padding: 16 }}>
      <div
        style={{
          fontSize: 'var(--text-12)',
          textTransform: 'uppercase',
          letterSpacing: '.08em',
          color: 'var(--faint)',
          marginBottom: 8,
        }}
      >
        Officer actions
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {btn('reclassify', 'Reclassify')}
        {btn('supersede', 'Mark as newer version…')}
        {btn('remove', 'Remove', true)}
      </div>

      {open === 'reclassify' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
          <div style={{ fontSize: 'var(--text-12)', color: 'var(--muted)' }}>
            Correct this document's type. Extraction and evidence re-run against the new type.
          </div>
          <select value={newType} onChange={(e) => setNewType(e.target.value)} style={inputStyle}>
            <option value="">choose the correct type…</option>
            {types.map((t) => (
              <option key={t} value={t}>
                {t.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
          <input
            placeholder="justification (optional)"
            value={why}
            onChange={(e) => setWhy(e.target.value)}
            style={inputStyle}
          />
          <div>{confirmBtn('reclassify', 'Reclassify document', !newType)}</div>
        </div>
      )}

      {open === 'supersede' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
          <div style={{ fontSize: 'var(--text-12)', color: 'var(--muted)' }}>
            This document is the newer version. Pick the older document it replaces — that one is
            marked superseded and drops out of active work.
          </div>
          <select
            value={supersedeTarget}
            onChange={(e) => setSupersedeTarget(e.target.value)}
            style={inputStyle}
          >
            <option value="">choose the older document…</option>
            {otherDocs.map((d) => (
              <option key={d.id} value={d.id}>
                {d.filename} ({humanizeToken(d.status)})
              </option>
            ))}
          </select>
          <input
            placeholder="justification (required)"
            value={why}
            onChange={(e) => setWhy(e.target.value)}
            style={inputStyle}
          />
          <div>
            {confirmBtn('supersede', 'Confirm supersession', !supersedeTarget || !why.trim())}
          </div>
        </div>
      )}

      {open === 'remove' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
          <div style={{ fontSize: 'var(--text-12)', color: 'var(--rust)' }}>
            Removes this document from all officer views. The stored file is retained for audit;
            this action is recorded permanently.
          </div>
          <input
            placeholder="justification (required)"
            value={why}
            onChange={(e) => setWhy(e.target.value)}
            style={inputStyle}
          />
          <div>{confirmBtn('remove', 'Remove document', !why.trim())}</div>
        </div>
      )}

      {msg && (
        <div
          style={{
            marginTop: 10,
            fontSize: 'var(--text-12)',
            color: msg.ok ? 'var(--ink)' : 'var(--rust)',
          }}
        >
          {msg.text}
        </div>
      )}
    </div>
  )
}

/* ── Focus (Ale ruling 2026-09-02) ──────────────────────────────────────
 * Arriving from the fund page with ?fund=&focus=, the document page names
 * where the officer came from, offers the way back to the SAME focus, and
 * lists what THIS document proves for the focused object — read from the
 * current record (the same object read the drawer uses), never a copy. */

interface FocusEvidenceRow {
  id: string
  document_id: string
  evidential_purpose: string
  evidential_weight: string
  validity_status: string
  valid_from: string | null
  valid_to: string | null
  source_page: number | null
  source_text: string | null
}

function useFocusContext(documentId: string | undefined) {
  const [params] = useSearchParams()
  const fundId = params.get('fund')
  const focusRaw = params.get('focus')
  const tab = params.get('tab') ?? 'record'
  const focus = useMemo(() => parseFocus(focusRaw), [focusRaw])
  const [table, setTable] = useState<RecordTablePayload | null>(null)
  const [rows, setRows] = useState<FocusEvidenceRow[] | null>(null)
  const [targetType, setTargetType] = useState<EvidenceTargetType>('entity')
  const [nature, setNature] = useState<string | null>(null)

  useEffect(() => {
    setTable(null)
    setRows(null)
    if (!fundId || !focus) return
    let cancelled = false
    apiFetch<RecordTablePayload>(`/api/funds/${fundId}/record-table?state=all`)
      .then(async (t) => {
        if (cancelled) return
        setTable(t)
        const target = targetForFocus(chainOf(t.segments), focus)
        if (!target) return
        const d = await apiFetch<{
          evidence: FocusEvidenceRow[]
          facts?: Record<string, string | number | boolean>
        }>(`/api/funds/${fundId}/objects/${target.type}/${target.id}`)
        if (cancelled) return
        setTargetType(target.type)
        setNature(d.facts?.nature != null ? String(d.facts.nature) : null)
        setRows(d.evidence.filter((e) => e.document_id === documentId))
      })
      .catch(() => {
        /* the focus is a convenience — the page stands without it */
      })
    return () => {
      cancelled = true
    }
  }, [fundId, focus, documentId])

  if (!fundId || !focus || !focusRaw) return null
  const name = table
    ? focusName(
        chainOf(table.segments),
        focus,
        table.fund_name,
        (eid) => table.entities.find((e) => e.id === eid)?.name ?? null
      )
    : null
  return {
    fundId,
    name,
    rows,
    targetType,
    nature,
    backHref: `/v2/funds/${encodeURIComponent(fundId)}?tab=${encodeURIComponent(tab)}&focus=${encodeURIComponent(focusRaw)}`,
  }
}

/* The cited passage, in English (Ale 2026-09-03: the excerpt left the
 * drawer for this card, and it must be readable). The translation is one
 * POST per passage, cached server-side by content hash and here per
 * session; behind the translation flag the original stands alone. */
const EXCERPT_CACHE = new Map<string, Promise<string>>()

function Excerpt({ text }: { text: string }) {
  const enabled = useTranslationEnabled()
  const [translated, setTranslated] = useState<string | null>(null)
  const [original, setOriginal] = useState(false)
  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    let p = EXCERPT_CACHE.get(text)
    if (!p) {
      p = translateText(text).then((r) => r.translated)
      EXCERPT_CACHE.set(text, p)
    }
    p.then((v) => {
      if (!cancelled && v && v.trim() && v.trim() !== text.trim()) setTranslated(v)
    }).catch(() => EXCERPT_CACHE.delete(text))
    return () => {
      cancelled = true
    }
  }, [enabled, text])
  return (
    <div
      style={{ fontSize: 'var(--text-12)', color: 'var(--muted)', marginTop: 4 }}
      data-testid="doc-excerpt"
    >
      {translated ? (
        <>
          <span style={{ color: 'var(--faint)', marginRight: 6 }}>translated</span>“{translated}”{' '}
          <button
            type="button"
            className="fa-tlink"
            style={{ fontSize: 'var(--text-12)', color: 'var(--accent-ink)' }}
            onClick={() => setOriginal((o) => !o)}
            data-testid="doc-excerpt-original"
          >
            {original ? 'hide the original' : 'original'}
          </button>
          {original ? (
            <div style={{ color: 'var(--faint)', fontStyle: 'italic', marginTop: 2 }}>“{text}”</div>
          ) : null}
        </>
      ) : (
        <>“{text}”</>
      )}
    </div>
  )
}

/** The card's order: the row the door cited (its page) first, the rest as
 *  the record lists them — the officer reads what they clicked before
 *  the rest of what the paper proves. */
function orderByCited<T extends { source_page: number | null }>(
  rows: T[],
  cited: number | null
): T[] {
  if (cited === null) return rows
  return [
    ...rows.filter((r) => r.source_page === cited),
    ...rows.filter((r) => r.source_page !== cited),
  ]
}

function FocusEvidenceCard({
  name,
  rows,
  filename,
  targetType,
  nature,
  cited,
  onPage,
}: {
  name: string | null
  rows: FocusEvidenceRow[] | null
  filename: string | null
  targetType: EvidenceTargetType
  nature: string | null
  /** The page the door cited — its row leads the card. */
  cited: number | null
  /** The page datum is a door: re-jump the viewer to it. */
  onPage: (page: number) => void
}) {
  const ordered = useMemo(() => (rows ? orderByCited(rows, cited) : null), [rows, cited])
  return (
    <div className="vcard" style={{ padding: 16 }} data-testid="doc-focus-card">
      <div
        style={{
          fontSize: 'var(--text-12)',
          textTransform: 'uppercase',
          letterSpacing: '.08em',
          color: 'var(--faint)',
          marginBottom: 8,
        }}
      >
        What this document proves for {name ?? 'the object in focus'}
      </div>
      {ordered === null ? (
        <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)' }}>reading the record…</div>
      ) : ordered.length === 0 ? (
        <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)' }}>
          Nothing on the record for this object cites this document.
        </div>
      ) : (
        ordered.map((ev) => (
          <div
            key={ev.id}
            data-testid="doc-focus-evidence"
            data-cited={cited !== null && ev.source_page === cited ? 'true' : undefined}
            style={{
              padding: '8px 10px',
              margin: '0 -4px 6px',
              borderRadius: 8,
              background: '#eef4fd',
              border: '1px solid #c9dbf5',
              fontSize: 'var(--text-12)',
              lineHeight: 1.55,
            }}
          >
            {/* S3 (2026-09-03): the same sentence the drawer speaks, so the
                officer reads the same words on both sides of the door; the
                page datum re-jumps the viewer. */}
            <EvidenceSentence
              ev={ev}
              target={targetType}
              nature={nature}
              filename={filename}
              onPage={onPage}
              pageTestId="doc-focus-page"
            />
            {ev.source_text ? <Excerpt text={ev.source_text} /> : null}
          </div>
        ))
      )}
    </div>
  )
}

function TraceRow({ t }: { t: Trace }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ borderBottom: '1px solid #f0f2f5', padding: '8px 0' }}>
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'baseline',
          cursor: 'pointer',
          fontSize: 'var(--text-12)',
        }}
        onClick={() => setOpen((o) => !o)}
      >
        <span style={{ fontWeight: 600 }}>
          {(t.pipeline_step ?? t.agent_name).replace(/_/g, ' ')}
        </span>
        {t.call_type && <span className="tag">{t.call_type.replace(/_/g, ' ')}</span>}
        <span
          className="mono"
          style={{ marginLeft: 'auto', fontSize: 'var(--text-12)', color: 'var(--faint)' }}
        >
          {t.latency_ms != null ? `${(t.latency_ms / 1000).toFixed(1)}s · ` : ''}
          {t.created_at.slice(11, 19)}
        </span>
        <span style={{ color: 'var(--faint)', fontSize: 'var(--text-12)' }}>
          {open ? '▾' : '▸'}
        </span>
      </div>
      {open && (
        <pre
          className="mono"
          style={{
            fontSize: 'var(--text-12)',
            background: '#fafbfc',
            border: '1px solid var(--line)',
            borderRadius: 7,
            padding: 10,
            marginTop: 6,
            maxHeight: 260,
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
          }}
        >
          {JSON.stringify(t.response_payload ?? {}, null, 2)}
        </pre>
      )}
    </div>
  )
}

export default function DocumentDetailPage() {
  const authSession = useAuthSession()
  const { id } = useParams<{ id: string }>()
  return <ScopedDocumentDetailPage key={JSON.stringify([authSession, id])} />
}

function ScopedDocumentDetailPage() {
  const authSession = useAuthSession()
  const location = useLocation()
  const requestedReturn = (location.state as { tracesReturn?: unknown } | null)?.tracesReturn
  const tracesReturn =
    typeof requestedReturn === 'string' && /^\/v2\/(traces|documents)(?:\?|$)/.test(requestedReturn)
      ? requestedReturn
      : '/v2/traces'
  const { id } = useParams<{ id: string }>()
  const [doc, setDoc] = useState<DocRow | null>(null)
  const [allDocs, setAllDocs] = useState<DocRow[]>([])
  const [traces, setTraces] = useState<Trace[]>([])
  const token = getToken()
  const isAdmin = token ? decodeToken(token)?.role === 'admin' : false
  const [error, setError] = useState<string | null>(null)
  const focusCtx = useFocusContext(id)
  /* The cited page from the door (`?page=N`, any door — with or without a
   * focus). `view` is what the viewer shows: the cited page on arrival, a
   * re-jump when the officer clicks a page datum; the URL stays the door
   * that opened the page. */
  const [params] = useSearchParams()
  const cited = parsePage(params.get('page'))
  const [view, setView] = useState<{ page: number | null; jump: number }>(() => ({
    page: cited,
    jump: 0,
  }))
  useEffect(() => {
    setView((v) => (v.page === cited ? v : { page: cited, jump: v.jump + 1 }))
  }, [cited])
  const jumpTo = (page: number) => setView((v) => ({ page, jump: v.jump + 1 }))

  useEffect(() => {
    if (!id) return
    const controller = new AbortController()
    setDoc(null)
    setError(null)
    setTraces([])
    // The list is paginated; old links must also work beyond its first page.
    fetchDocuments({ signal: controller.signal })
      .then(async (all) => {
        if (controller.signal.aborted) return
        setAllDocs(all)
        const found = all.find((d) => d.id === id)
        if (found) {
          setDoc(found)
          return
        }
        const removed = await fetchDocuments({ signal: controller.signal }, 'removed')
        if (controller.signal.aborted) return
        const archived = removed.find((d) => d.id === id)
        if (archived) setDoc(archived)
        else setError('document not found')
      })
      .catch((e) => {
        if (!controller.signal.aborted) setError(e instanceof Error ? e.message : 'failed to load')
      })
    if (isAdmin) {
      apiFetch<{ traces: Trace[] }>(`/api/reasoning-traces/document/${id}`, {
        signal: controller.signal,
      })
        .then((r) => {
          if (!controller.signal.aborted) setTraces(r.traces)
        })
        .catch(() => {})
    }
    return () => controller.abort()
  }, [id, isAdmin, authSession])

  if (!doc)
    return (
      <main className="detail">
        <p role={error ? 'alert' : 'status'}>{error ?? 'Loading document…'}</p>
        <Link to="/v2/traces">Traces</Link>
      </main>
    )

  return (
    <main className="detail">
      <div className="dhead" style={{ gap: 10 }}>
        {/* Shared rail Back returns to the originating card; this is the
            explicit Traces destination, preserving its list filters. */}
        <Link
          to={tracesReturn}
          style={{ fontSize: 'var(--text-12)', color: 'var(--accent-ink)', textDecoration: 'none' }}
        >
          {/* The global page is named Traces (Ale, 6 Sep 2026; URLs unchanged) —
              the crumb carries the nav name. Evidence is the fund-level tab, not this page. */}
          Traces
        </Link>
        <h1 style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {doc?.filename ?? '…'}
        </h1>
        <HelpDot k="surface.document_detail" />
        {doc && id ? (
          <button
            type="button"
            data-testid="doc-download"
            onClick={async () => {
              const token = localStorage.getItem('nolos_jwt')
              const r = await fetch(`/api/documents/${id}/content`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
              })
              if (!r.ok) return
              const blob = await r.blob()
              const a = document.createElement('a')
              a.href = URL.createObjectURL(blob)
              a.download = doc.filename || 'document.pdf'
              a.click()
              URL.revokeObjectURL(a.href)
            }}
            style={{
              marginLeft: 'auto',
              fontSize: 'var(--text-12)',
              color: 'var(--accent-ink)',
              background: 'none',
              border: '1px solid var(--line)',
              borderRadius: 8,
              padding: '4px 10px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Download PDF
          </button>
        ) : null}
      </div>
      {focusCtx ? (
        <div
          data-testid="doc-focus-line"
          style={{
            display: 'flex',
            gap: 6,
            alignItems: 'baseline',
            fontSize: 'var(--text-12)',
            padding: '6px 18px',
            background: '#eef4fd',
            borderBottom: '1px solid #c9dbf5',
            color: 'var(--ink)',
          }}
        >
          opened from <b>{focusCtx.name ?? '…'}</b>
          <span style={{ color: 'var(--faint)' }}>·</span>
          <Link
            to={focusCtx.backHref}
            data-testid="doc-focus-back"
            style={{ color: 'var(--accent-ink)', textDecoration: 'none', fontWeight: 600 }}
          >
            back to the fund
          </Link>
        </div>
      ) : null}
      {/* flexWrap + a real minWidth on the viewer: with the facts column
          fixed at 440px, a narrow window crushed the PDF to a ~45px
          sliver (real-browser walk find, 2026-08-31) — now it stacks. */}
      <div className="dbody" style={{ alignItems: 'stretch', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 320, minHeight: 480 }}>
          {id && <Pdf documentId={id} page={view.page} jump={view.jump} />}
        </div>

        <div
          style={{
            width: 440,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            overflowY: 'auto',
          }}
        >
          {error && <div style={{ fontSize: 'var(--text-12)', color: 'var(--gap)' }}>{error}</div>}

          {focusCtx ? (
            <FocusEvidenceCard
              name={focusCtx.name}
              rows={focusCtx.rows}
              filename={doc?.filename ?? null}
              targetType={focusCtx.targetType}
              nature={focusCtx.nature}
              cited={cited}
              onPage={jumpTo}
            />
          ) : null}

          {doc && (
            <ActionsCard
              doc={doc}
              allDocs={allDocs}
              onApplied={(update) => setDoc((d) => (d ? { ...d, ...update } : d))}
            />
          )}

          <div className="vcard" style={{ padding: 16 }}>
            {doc && (
              <Sentence
                as="div"
                className="sn-lead"
                data-testid="doc-detail-header"
                meaning="document.detail_header"
                values={{
                  document: doc.filename,
                  type: doc.document_type,
                  pages: doc.page_count,
                  date: doc.upload_date,
                  fund: doc.fund_names?.join(', ') || null,
                  status: doc.status,
                }}
              />
            )}
            {/* G4 — source-language advisory (champion-2 #816). Mirrors the v1
                ChainReviewPage chip; only a detected non-English source shows,
                nudging the officer to verify figures against the original. */}
            {doc?.source_language && doc.source_language !== 'en' && (
              <div
                data-testid="source-language-chip"
                style={{
                  marginTop: 10,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 6,
                  fontSize: 'var(--text-12)',
                  color: '#a35d17',
                  background: 'rgba(163,93,23,0.10)',
                  borderRadius: 7,
                  padding: '7px 9px',
                }}
              >
                <AlertTriangle size={13} style={{ marginTop: 1, flexShrink: 0 }} />
                <span>{doc.source_language.toUpperCase()} source — verify figures</span>
              </div>
            )}
            {/* Gap 7 — why the pipeline could not read this document. The
                encrypted cause gets its own words because the fix differs
                (remove the password vs re-scan/replace the file). */}
            {doc &&
              (doc.status === 'unprocessable' || doc.status === 'failed') &&
              doc.failure_reason && (
                <div
                  data-testid="failure-reason-chip"
                  style={{
                    marginTop: 10,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 6,
                    fontSize: 'var(--text-12)',
                    color: 'var(--gap)',
                    background: 'rgba(190,50,50,0.08)',
                    borderRadius: 7,
                    padding: '7px 9px',
                  }}
                >
                  <AlertTriangle size={13} style={{ marginTop: 1, flexShrink: 0 }} />
                  <span>
                    {humanizeToken(doc.status)} — {failureLabel(doc.failure_reason)}
                    {doc.failure_reason === 'encrypted'
                      ? '. Remove the password and re-upload.'
                      : ''}
                  </span>
                </div>
              )}
          </div>

          {id && <PaymentObservationsCard documentId={id} onPage={jumpTo} />}
          {id && <TermsReviewCard documentId={id} />}
          {id && <PipelinePanel documentId={id} />}

          {isAdmin && (
            <div className="vcard" style={{ padding: 16 }}>
              <div
                style={{
                  fontSize: 'var(--text-12)',
                  textTransform: 'uppercase',
                  letterSpacing: '.08em',
                  color: 'var(--faint)',
                  marginBottom: 4,
                }}
              >
                What the pipeline did · {traces.length} steps
              </div>
              {traces.length === 0 && (
                <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)' }}>
                  No pipeline work recorded — the document may still be queued, or it predates trace
                  capture.
                </div>
              )}
              {traces.map((t) => (
                <TraceRow key={t.id} t={t} />
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  )
}
